import * as React from 'react';
import { Link } from 'react-router-dom';

import Header from '../components/Header';
import { TOOLS } from '../tools';

/**
 * MOPS landing page. Renders a tile grid driven by the `TOOLS` registry, one
 * tile per admin tool. The command palette (Cmd+K, wired in `MainApp`) is the
 * keyboard-first path to the same tools; the tile grid is the overview.
 *
 * Density-first: compact tiles, borders over backgrounds, single accent on
 * hover. Operators who know the tool they want reach for Cmd+K; tiles exist
 * for discoverability and visual inventory.
 */
const HomeScene = () => {
  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-4 px-6 py-5">
        <header className="flex items-baseline gap-3">
          <h1 className="text-sm font-semibold uppercase tracking-widest">Tools</h1>
          <span className="text-fg-tertiary font-mono text-xs">
            {TOOLS.length} {TOOLS.length === 1 ? 'tool' : 'tools'}
          </span>
          <span className="text-fg-quaternary ml-auto text-xs">
            Press <KbdHint>⌘</KbdHint>
            <KbdHint>K</KbdHint> to jump
          </span>
        </header>

        <section aria-label="Admin tools" className="grid grid-cols-2 gap-3 bg-transparent xl:grid-cols-3">
          {TOOLS.map((tool) => {
            const Icon = tool.icon;
            return (
              <Link
                key={tool.id}
                to={tool.path}
                className="border-border-primary bg-bg-primary hover:bg-bg-secondary hover:border-border-brand focus-visible:outline-border-brand group flex items-center gap-3 border px-3 py-2.5 transition-colors focus-visible:outline-2 focus-visible:-outline-offset-1"
              >
                <Icon className="text-fg-secondary group-hover:text-fg-brand-primary h-7 w-7 shrink-0 transition-colors" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="text-fg-primary group-hover:text-fg-brand-primary truncate text-sm font-semibold transition-colors">
                    {tool.name}
                  </span>
                  <span className="text-fg-tertiary truncate text-xs">{tool.description}</span>
                </span>
                <span className="text-fg-quaternary shrink-0 font-mono text-[10px] uppercase tracking-wider">
                  {tool.path}
                </span>
              </Link>
            );
          })}
        </section>
      </main>
    </div>
  );
};

interface IKbdHintProps {
  children: React.ReactNode;
}

const KbdHint = ({ children }: IKbdHintProps) => (
  <kbd className="border-border-primary bg-bg-primary text-fg-tertiary mx-0.5 inline-block rounded-sm border px-1 py-px font-mono text-[10px] leading-none">
    {children}
  </kbd>
);

export default HomeScene;
